import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import NiceModal from "@ebay/nice-modal-react";
import UploadedPOSA from "@tootsweet/model/entities/UploadedPOSA";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import KC_Heading2 from "../../../../../../designsystem/typography/KC-Heading/KC_Heading2";
import Separator from "../../../../../../components/Separator";
import { tt } from "../../../../../../utils/i18n/i18n";
import POSAItem from "../POSAItem";

import { useAccountToDisplaySelector } from "../../../../../../redux/actions/user";
import KC_Text from "../../../../../../designsystem/typography/KC_Text/KC_Text";
import {
  KC_Text_Sizes,
  KC_Weights
} from "../../../../../../designsystem/ds_commons";
import {
  getHandToHandDescription,
  getTableSupportDescription,
  getDeliveryBagDescription
} from "./posaQuantities";

import ExistingPOSASection from "./ExistingPOSASection";
import PreviewModal from "./PreviewModal";
import POSAApi from "../../../../../12_POSAManager/api";
import SupportSection from "./SupportSection";
import KC_Button, {
  KC_ButtonSize
} from "../../../../../../designsystem/KC_Button/KC_Button";
import TwemojiWrapper from "../../../../../../components/TwemojiWrapper/TwemojiWrapper";
import KC_Heading1 from "../../../../../../designsystem/typography/KC-Heading/KC_Heading1";
import "./styles.scss";

interface POSAFormPreviewProps {
  onRetake: () => void;
}

export default function POSAFormPreview(props: POSAFormPreviewProps) {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const selectedType = params.get("type");

  const { accountToDisplay } = useAccountToDisplaySelector();

  const formAnswers = accountToDisplay?.businessUser?.posaFormAnswers;

  const tableSupports = formAnswers?.tableSupports || [];
  const deliveryBagSupports = formAnswers?.deliveryBagSupports || [];
  const establishmentSupports = formAnswers?.establishmentSupports || [];

  const [uploadedPOSAList, setUploadedPOSAList] = useState<UploadedPOSA[]>([]);

  const displayBusinessCards = useMemo(() => {
    return (
      accountToDisplay?.businessUser?.posaFormAnswers?.hasDineInService ||
      (accountToDisplay?.businessUser?.posaFormAnswers?.hasDineInService &&
        accountToDisplay?.businessUser?.posaFormAnswers?.hasDeliveryService)
    );
  }, [accountToDisplay]);

  async function fetchUploadedPOSA() {
    if (!accountToDisplay?.trackingID || !accountToDisplay?.token) return;

    try {
      const list = await POSAApi.getUploadedPOSA(
        accountToDisplay.trackingID,
        accountToDisplay.token
      );
      setUploadedPOSAList(list);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    void fetchUploadedPOSA();
  }, [accountToDisplay?.trackingID, accountToDisplay?.token]);

  useEffect(() => {
    if (!selectedType || uploadedPOSAList.length === 0) return;

    const match = uploadedPOSAList.find((posa) => posa.type === selectedType);
    if (!match) return;

    void NiceModal.show(PreviewModal, {
      uploadedPOSA: match,
      onRefresh: fetchUploadedPOSA,
      onClose: () => {
        const newParams = new URLSearchParams(location.search);
        newParams.delete("type");
        const newSearch = newParams.toString();
        history.replace(location.pathname + (newSearch ? `?${newSearch}` : ""));
      }
    });
  }, [selectedType, uploadedPOSAList]);

  return (
    <div>
      <div className="d-flex flex-column flex-column-reverse flex-md-row mt-4">
        <TwemojiWrapper isMediumText={true}>
          <KC_Heading1 text={tt("posa_form_preview_title")} className="mb-2" />
        </TwemojiWrapper>

        <KC_Button
          id="posa-form-retake"
          size={KC_ButtonSize.Large}
          text={tt("posa_form_retake")}
          isInverted={true}
          showIconOnTheLeft={true}
          icon={faRefresh}
          onClick={props.onRetake}
          className="d-flex ml-auto mb-4 retake-form"
        />
      </div>
      <Separator />

      {displayBusinessCards && (
        <div className="mt-3 mb-3">
          <KC_Heading2
            text={tt("posa_form_preview_hand_to_hand")}
            className="mb-2"
          />
          <KC_Text
            text={getHandToHandDescription(
              accountToDisplay?.businessUser?.clientsPerDay || "",
              "businesscardsstandard"
            )}
            size={KC_Text_Sizes.md}
            weight={KC_Weights.reg}
          />
          <POSAItem
            format="businesscardsstandard"
            hideDescription
            hidePlaceholderTitle
          />
          <Separator />
        </div>
      )}

      {formAnswers?.existingPOSA && (
        <ExistingPOSASection
          existingPOSA={formAnswers.existingPOSA}
          uploadedPOSAList={uploadedPOSAList}
          onRefresh={fetchUploadedPOSA}
        />
      )}

      <SupportSection
        hidePlaceholderTitle
        items={tableSupports}
        title={tt("posa_form_preview_table_presence")}
        description={getTableSupportDescription(
          tableSupports[0]?.format || "",
          formAnswers?.numberOfTables || 0
        )}
      />

      <SupportSection
        hidePlaceholderTitle
        items={deliveryBagSupports}
        title={tt("posa_form_preview_delivery_bag_presence")}
        description={getDeliveryBagDescription(
          deliveryBagSupports[0]?.format || "",
          formAnswers?.deliveryClientsPerDay || ""
        )}
      />

      <SupportSection
        items={establishmentSupports}
        title={tt("posa_form_preview_establishment")}
      />
    </div>
  );
}
